import { useCallback, useEffect, useRef, useState } from "react";
import {
  Inviter,
  Registerer,
  RegistererState,
  Session,
  SessionState,
  UserAgent,
  Web
} from "sip.js";
import {
  CallCredentials,
  CallSessionError,
  clearCallCredentials,
  endCallSession,
  postIcePathEvent,
  postCallEvent,
  waitUntilReady
} from "../rtc/callSession";
import { readSelectedIcePath } from "../rtc/icePath";
import { PreparedMedia } from "../rtc/preparedMedia";
import { abortError, parallelSetup } from "../rtc/parallelSetup";
import { createSetupTrace, SetupTrace } from "../rtc/setupTrace";

export type CallState = "idle" | "connecting" | "registered" | "calling" | "incall" | "ended" | "error";
export type CallErrorCode = "RECIPIENT_UNAVAILABLE" | "SESSION_EXPIRED" | "NO_ANSWER" | "MICROPHONE_PERMISSION" | "CONNECTION_FAILED";

const UNREGISTER_GRACE_MS = 500;

function wait(milliseconds: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, milliseconds));
}

function callErrorCode(error: unknown): CallErrorCode {
  if (error instanceof CallSessionError) return error.code;
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error || "");
  if (/notallowed|permission|microphone|media/i.test(message)) return "MICROPHONE_PERMISSION";
  if (/timeout|timed out|no answer/i.test(message)) return "NO_ANSWER";
  if (/missing|incomplete|expired|invalid.*configuration/i.test(message)) return "SESSION_EXPIRED";
  return "CONNECTION_FAILED";
}

async function setLocalVideo(session: Session, enable: boolean): Promise<void> {
  const handler = session.sessionDescriptionHandler;
  if (!(handler instanceof Web.SessionDescriptionHandler) || !handler.peerConnection) {
    throw new Error("Media connection is unavailable");
  }
  const peerConnection = handler.peerConnection;
  const transceiver = peerConnection.getTransceivers().find(item =>
    item.sender.track?.kind === "video" || item.receiver.track?.kind === "video"
  );
  if (enable) {
    const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
    const cameraTrack = cameraStream.getVideoTracks()[0];
    if (!cameraTrack) throw new Error("The camera did not provide a video track");
    if (transceiver) {
      await transceiver.sender.replaceTrack(cameraTrack);
      transceiver.direction = "sendrecv";
    } else {
      peerConnection.addTrack(cameraTrack, handler.localMediaStream);
    }
    handler.localMediaStream.addTrack(cameraTrack);
  } else {
    const localTrack = handler.localMediaStream.getVideoTracks()[0];
    if (transceiver) {
      await transceiver.sender.replaceTrack(null);
      transceiver.direction = "recvonly";
    }
    if (localTrack) {
      handler.localMediaStream.removeTrack(localTrack);
      localTrack.stop();
    }
  }
  await session.invite();
}

function waitForRegistration(registerer: Registerer, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortError());
  if (registerer.state === RegistererState.Registered) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeout);
      registerer.stateChange.removeListener(listener);
      signal?.removeEventListener("abort", abort);
    };
    const abort = () => { cleanup(); reject(abortError()); };
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("SIP registration timed out"));
    }, 10_000);
    const listener = (state: RegistererState) => {
      if (state === RegistererState.Registered) {
        cleanup();
        resolve();
      } else if (state === RegistererState.Terminated) {
        cleanup();
        reject(new Error("SIP registration failed"));
      }
    };
    registerer.stateChange.addListener(listener);
    signal?.addEventListener("abort", abort, { once: true });
  });
}

async function reportIcePath(session: Session, credentials: CallCredentials): Promise<void> {
  const handler = session.sessionDescriptionHandler;
  if (!(handler instanceof Web.SessionDescriptionHandler) || !handler.peerConnection) return;
  const telemetry = await readSelectedIcePath(handler.peerConnection);
  if (telemetry) await postIcePathEvent(credentials, telemetry);
}

export function useSipClient(displayName = "Bcall User") {
  const [callState, setCallState] = useState<CallState>("idle");
  const [errorCode, setErrorCode] = useState<CallErrorCode | null>(null);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [remoteVideoEnabled, setRemoteVideoEnabled] = useState(false);
  const [muted, setMuted] = useState(false);
  const userAgentRef = useRef<UserAgent | null>(null);
  const registererRef = useRef<Registerer | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const credentialsRef = useRef<CallCredentials | null>(null);
  const endingRef = useRef(false);
  const setupAbortRef = useRef<AbortController | null>(null);
  const preparedMediaRef = useRef<PreparedMedia | null>(null);
  const traceRef = useRef<SetupTrace>(() => {});

  const attachMedia = useCallback((session: Session) => {
    const handler = session.sessionDescriptionHandler;
    if (!(handler instanceof Web.SessionDescriptionHandler)) return;
    const remoteAudio = document.getElementById("remoteAudio") as HTMLAudioElement | null;
    const remoteVideo = document.getElementById("remoteVideo") as HTMLVideoElement | null;
    const localVideo = document.getElementById("localVideo") as HTMLVideoElement | null;
    const sync = () => {
      if (remoteAudio && remoteAudio.srcObject !== handler.remoteMediaStream) remoteAudio.srcObject = handler.remoteMediaStream;
      if (remoteVideo && remoteVideo.srcObject !== handler.remoteMediaStream) remoteVideo.srcObject = handler.remoteMediaStream;
      if (localVideo && localVideo.srcObject !== handler.localMediaStream) localVideo.srcObject = handler.localMediaStream;
      setRemoteVideoEnabled(handler.remoteMediaStream.getVideoTracks().some(track => track.readyState === "live"));
      setVideoEnabled(handler.localMediaStream.getVideoTracks().some(track => track.readyState === "live"));
    };
    sync();
    handler.remoteMediaStream.addEventListener("addtrack", sync);
    handler.remoteMediaStream.addEventListener("removetrack", sync);
    handler.localMediaStream.addEventListener("addtrack", sync);
    handler.localMediaStream.addEventListener("removetrack", sync);
  }, []);

  const resetCallMedia = useCallback(() => {
    sessionRef.current = null;
    setVideoEnabled(false);
    setRemoteVideoEnabled(false);
    setMuted(false);
  }, []);

  const markCallEnded = useCallback(() => {
    resetCallMedia();
    clearCallCredentials();
    setCallState("ended");
  }, [resetCallMedia]);

  const stopTransport = useCallback(async () => {
    setupAbortRef.current?.abort();
    setupAbortRef.current = null;
    preparedMediaRef.current?.close();
    preparedMediaRef.current = null;
    const registerer = registererRef.current;
    const userAgent = userAgentRef.current;
    registererRef.current = null;
    userAgentRef.current = null;
    if (registerer && registerer.state === RegistererState.Registered) {
      await Promise.race([
        registerer.unregister().catch(() => {}),
        wait(UNREGISTER_GRACE_MS)
      ]);
    }
    if (userAgent) await userAgent.stop().catch(() => {});
  }, []);

  const connectAndCall = useCallback(async (credentials: CallCredentials) => {
    const setup = new AbortController();
    setupAbortRef.current = setup;
    const trace = createSetupTrace(); traceRef.current = trace; trace("setup-start");
    let setupTimedOut = false;
    const setupTimeout = window.setTimeout(() => { setupTimedOut = true; setup.abort(); }, 30000);
    credentialsRef.current = credentials;
    endingRef.current = false;
    setCallState("connecting");
    setErrorCode(null);
    try {
      const uri = UserAgent.makeURI(`sip:${credentials.user}@${credentials.domain}`);
      const target = UserAgent.makeURI(`sip:${credentials.targetSipUser}@${credentials.domain}`);
      if (!uri || !target) throw new Error("Invalid SIP address");

      let prepared: PreparedMedia;
      let preparedClaimed = false;
      const defaultFactory = Web.defaultSessionDescriptionHandlerFactory();
      const userAgent = new UserAgent({
        uri,
        displayName,
        gracefulShutdown: false,
        authorizationUsername: credentials.authorizationUsername || credentials.user,
        authorizationPassword: credentials.pass,
        transportOptions: { server: credentials.websocket },
        sessionDescriptionHandlerFactory: (session, options) => {
          if (!preparedClaimed) { preparedClaimed = true; return prepared; }
          return defaultFactory(session, options);
        },
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: { iceServers: credentials.iceServers }
        },
        noAnswerTimeout: 45,
        logBuiltinEnabled: process.env.NODE_ENV !== "production"
      });
      const registerer = new Registerer(userAgent, { expires: 180, refreshFrequency: 90 });
      userAgentRef.current = userAgent;
      registererRef.current = registerer;
      prepared = new PreparedMedia(userAgent.getLogger("bcall.media"), Web.defaultMediaStreamFactory(), {
        iceGatheringTimeout: 5000,
        peerConnectionConfiguration: { ...Web.defaultPeerConnectionConfiguration(), iceServers: credentials.iceServers, iceCandidatePoolSize: 1 }
      });
      prepared.setTrace(trace); preparedMediaRef.current = prepared;
      await parallelSetup([
        () => prepared.prepare(),
        async () => {
          trace("socket-start"); await userAgent.start(); trace("socket-ready");
          if (setup.signal.aborted) throw abortError();
          const registered = waitForRegistration(registerer, setup.signal);
          trace("register-start");
          await Promise.all([Promise.resolve().then(() => registerer.register()), registered]);
          trace("register-ready");
          if (!setup.signal.aborted) setCallState("registered");
        },
        async () => {
          trace(credentials.lateReceiverJoin ? "receivers-late-join-enabled" : "receivers-wait-start");
          await waitUntilReady(credentials, () => endingRef.current, setup.signal);
          trace("receivers-ready");
        }
      ], setup.signal);
      if (endingRef.current || setup.signal.aborted) return;

      const inviter = new Inviter(userAgent, target, {
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
        sessionDescriptionHandlerOptionsReInvite: { constraints: { audio: true, video: false } }
      });
      sessionRef.current = inviter;
      inviter.stateChange.addListener((state) => {
        if (state === SessionState.Establishing) setCallState("calling");
        if (state === SessionState.Established) {
          trace("sip-established");
          setCallState("incall");
          attachMedia(inviter);
          void reportIcePath(inviter, credentials);
        }
        if (state === SessionState.Terminated) {
          markCallEnded();
          if (!endingRef.current) {
            endingRef.current = true;
            void Promise.allSettled([
              endCallSession(credentialsRef.current),
              stopTransport()
            ]);
          }
        }
      });
      trace("invite-start");
      await inviter.invite();
      trace("invite-sent");
    } catch (error) {
      if (endingRef.current) {
        await stopTransport();
        return;
      }
      const failure = setupTimedOut ? new Error("Call setup timed out") : error;
      const message = failure instanceof Error ? failure.message : "Connection failed";
      trace("setup-failed", callErrorCode(failure));
      setErrorCode(callErrorCode(failure));
      setCallState("error");
      await stopTransport();
      await postCallEvent(credentialsRef.current, "failed", message);
      clearCallCredentials();
    } finally { window.clearTimeout(setupTimeout); }
  }, [attachMedia, displayName, markCallEnded, stopTransport]);

  const hangup = useCallback(() => {
    if (endingRef.current) return;
    endingRef.current = true;
    traceRef.current("local-hangup");
    setupAbortRef.current?.abort();
    preparedMediaRef.current?.close();
    const session = sessionRef.current;
    const credentials = credentialsRef.current;
    markCallEnded();

    const sipTeardown = session?.state === SessionState.Established
      ? session.bye().then(() => undefined).catch(() => {})
      : session instanceof Inviter
        ? session.cancel().then(() => undefined).catch(() => {})
        : Promise.resolve();

    void (async () => {
      await sipTeardown;
      await Promise.allSettled([
        endCallSession(credentials),
        stopTransport()
      ]);
    })();
  }, [markCallEnded, stopTransport]);

  const toggleVideo = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || session.state !== SessionState.Established) return;
    const enable = !videoEnabled;
    try {
      await setLocalVideo(session, enable);
      setVideoEnabled(enable);
      if (enable) await postCallEvent(credentialsRef.current, "video-enabled");
      window.setTimeout(() => attachMedia(session), 250);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to change video";
      console.error("Video toggle failed", message);
      setErrorCode(callErrorCode(error));
    }
  }, [attachMedia, videoEnabled]);

  const toggleMute = useCallback(() => {
    const handler = sessionRef.current?.sessionDescriptionHandler;
    if (!(handler instanceof Web.SessionDescriptionHandler)) return;
    const nextMuted = !muted;
    handler.localMediaStream.getAudioTracks().forEach(track => { track.enabled = !nextMuted; });
    setMuted(nextMuted);
  }, [muted]);

  const sendDTMF = useCallback((tone: string) => {
    const session = sessionRef.current;
    if (!session || session.state !== SessionState.Established) return;
    session.info({
      requestOptions: {
        body: {
          contentDisposition: "render",
          contentType: "application/dtmf-relay",
          content: `Signal=${tone}\r\nDuration=100`
        }
      }
    }).catch(() => {});
  }, []);

  useEffect(() => () => {
    endingRef.current = true;
    const session = sessionRef.current;
    if (session?.state === SessionState.Established) session.bye().catch(() => {});
    endCallSession(credentialsRef.current);
    stopTransport();
    clearCallCredentials();
  }, [stopTransport]);

  return {
    callState,
    errorCode,
    videoEnabled,
    remoteVideoEnabled,
    muted,
    connectAndCall,
    hangup,
    sendDTMF,
    toggleMute,
    toggleVideo
  };
}
